import React from 'react';
import { Cartesian3DAxes } from '../common/Cartesian3DAxes.jsx';
import { DraggablePoint } from '../common/DraggablePoint.jsx';

export const CuboidDiagonalsComponent = ({ params, onParamChange, svgRef, showCoordinates }) => {
  const {
    centerX = 200,
    centerY = 150,
    width = 100,
    height = 80,
    depth = 60,
    baseFill = "lightgray",
    sideFill = "lightblue",
    topFill = "lightgreen",
    stroke = "black",
    strokeWidth = 2,
    showFaceDiagonals = true,
    showSpaceDiagonals = true,
    showDimensions = true,
    show3DAxes = true
  } = params;

  // Calculate 3D projection (isometric-like view)
  const depthOffsetX = depth * 0.5;
  const depthOffsetY = depth * 0.3;
  
  // Front face vertices
  const frontBottomLeft = { x: centerX - width / 2, y: centerY + height / 2 };
  const frontBottomRight = { x: centerX + width / 2, y: centerY + height / 2 };
  const frontTopLeft = { x: centerX - width / 2, y: centerY - height / 2 };
  const frontTopRight = { x: centerX + width / 2, y: centerY - height / 2 };
  
  // Back face vertices (with depth offset)
  const backBottomLeft = { x: frontBottomLeft.x - depthOffsetX, y: frontBottomLeft.y - depthOffsetY };
  const backBottomRight = { x: frontBottomRight.x - depthOffsetX, y: frontBottomRight.y - depthOffsetY };
  const backTopLeft = { x: frontTopLeft.x - depthOffsetX, y: frontTopLeft.y - depthOffsetY };
  const backTopRight = { x: frontTopRight.x - depthOffsetX, y: frontTopRight.y - depthOffsetY };

  return (
    <g>
      {/* 3D Axes */}
      {show3DAxes && <Cartesian3DAxes centerX={centerX} centerY={centerY + height} size={50} />}
      
      {/* Back faces (drawn first for depth) */}
      {/* Back face */}
      <polygon
        points={`${backBottomLeft.x},${backBottomLeft.y} ${backBottomRight.x},${backBottomRight.y} ${backTopRight.x},${backTopRight.y} ${backTopLeft.x},${backTopLeft.y}`}
        fill={baseFill}
        stroke={stroke}
        strokeWidth={strokeWidth}
        opacity="0.5"
      />
      
      {/* Top face */}
      <polygon
        points={`${frontTopLeft.x},${frontTopLeft.y} ${frontTopRight.x},${frontTopRight.y} ${backTopRight.x},${backTopRight.y} ${backTopLeft.x},${backTopLeft.y}`}
        fill={topFill}
        stroke={stroke}
        strokeWidth={strokeWidth}
      />
      
      {/* Left side face */}
      <polygon
        points={`${frontBottomLeft.x},${frontBottomLeft.y} ${backBottomLeft.x},${backBottomLeft.y} ${backTopLeft.x},${backTopLeft.y} ${frontTopLeft.x},${frontTopLeft.y}`}
        fill={sideFill}
        stroke={stroke}
        strokeWidth={strokeWidth}
      />
      
      {/* Front face */}
      <polygon
        points={`${frontBottomLeft.x},${frontBottomLeft.y} ${frontBottomRight.x},${frontBottomRight.y} ${frontTopRight.x},${frontTopRight.y} ${frontTopLeft.x},${frontTopLeft.y}`}
        fill={baseFill}
        stroke={stroke}
        strokeWidth={strokeWidth * 1.5}
      />
      
      {/* Connecting edges to back */}
      <line x1={frontBottomRight.x} y1={frontBottomRight.y} x2={backBottomRight.x} y2={backBottomRight.y} stroke={stroke} strokeWidth={strokeWidth} />
      <line x1={frontTopRight.x} y1={frontTopRight.y} x2={backTopRight.x} y2={backTopRight.y} stroke={stroke} strokeWidth={strokeWidth} />
      
      {/* Face diagonals */}
      {showFaceDiagonals && (
        <>
          {/* Front face diagonals */}
          <line x1={frontBottomLeft.x} y1={frontBottomLeft.y} x2={frontTopRight.x} y2={frontTopRight.y} stroke="blue" strokeWidth={strokeWidth} strokeDasharray="3,3" />
          <line x1={frontBottomRight.x} y1={frontBottomRight.y} x2={frontTopLeft.x} y2={frontTopLeft.y} stroke="blue" strokeWidth={strokeWidth} strokeDasharray="3,3" />
        </>
      )}
      
      {/* Space diagonals */}
      {showSpaceDiagonals && (
        <>
          <line x1={frontBottomLeft.x} y1={frontBottomLeft.y} x2={backTopRight.x} y2={backTopRight.y} stroke="red" strokeWidth={strokeWidth} strokeDasharray="5,5" />
          <line x1={frontBottomRight.x} y1={frontBottomRight.y} x2={backTopLeft.x} y2={backTopLeft.y} stroke="red" strokeWidth={strokeWidth} strokeDasharray="5,5" />
          <line x1={frontTopLeft.x} y1={frontTopLeft.y} x2={backBottomRight.x} y2={backBottomRight.y} stroke="red" strokeWidth={strokeWidth} strokeDasharray="5,5" />
          <line x1={frontTopRight.x} y1={frontTopRight.y} x2={backBottomLeft.x} y2={backBottomLeft.y} stroke="red" strokeWidth={strokeWidth} strokeDasharray="5,5" />
        </>
      )}
      
      {/* Dimensions */}
      {showDimensions && (
        <>
          <text x={centerX} y={centerY + height / 2 + 25} fontSize="11" fill="black" textAnchor="middle">
            w = {width.toFixed(0)}
          </text>
          <text x={centerX - width / 2 - 15} y={centerY} fontSize="11" fill="black">
            h = {height.toFixed(0)}
          </text>
          <text x={centerX - width / 2 - depthOffsetX / 2} y={centerY + height / 2 - depthOffsetY / 2 + 5} fontSize="11" fill="black">
            d = {depth.toFixed(0)}
          </text>
        </>
      )}
      
      {/* Draggable points */}
      {onParamChange && (
        <>
          <DraggablePoint
            cx={frontBottomRight.x}
            cy={frontBottomRight.y}
            label="W"
            coords={showCoordinates ? `w=${width.toFixed(0)}` : ''}
            onDrag={(newX, newY) => {
              const newWidth = 2 * (newX - centerX);
              onParamChange('width', Math.max(40, Math.min(200, newWidth)));
            }}
            svgRef={svgRef}
          />
          <DraggablePoint
            cx={frontTopLeft.x}
            cy={frontTopLeft.y}
            label="H"
            coords={showCoordinates ? `h=${height.toFixed(0)}` : ''}
            onDrag={(newX, newY) => {
              const newHeight = 2 * (centerY - newY);
              onParamChange('height', Math.max(30, Math.min(150, newHeight)));
            }}
            svgRef={svgRef}
          />
          <DraggablePoint
            cx={backBottomLeft.x}
            cy={backBottomLeft.y}
            label="D"
            coords={showCoordinates ? `d=${depth.toFixed(0)}` : ''}
            onDrag={(newX, newY) => {
              const newDepth = 2 * (frontBottomLeft.x - newX);
              onParamChange('depth', Math.max(20, Math.min(120, newDepth)));
            }}
            svgRef={svgRef}
          />
        </>
      )}
    </g>
  );
};
